import React, { createContext, useContext, useState, useCallback } from 'react';
import { toast } from 'react-toastify';
import './SubscriptionProgressToast.css';

export const SubscriptionProgressContext = createContext();

export const useSubscriptionProgress = () => {
  const context = useContext(SubscriptionProgressContext);
  if (!context) {
    throw new Error('useSubscriptionProgress must be used within SubscriptionProgressProvider');
  }
  return context;
};

export const SubscriptionProgressProvider = ({ children }) => {
  const [subscribingProducts, setSubscribingProducts] = useState([]); // Array of { id, sku, status }
  const [toastId, setToastId] = useState(null);
  const [isToastHidden, setIsToastHidden] = useState(false);

  // Add a product to subscription tracking
  const addSubscribingProduct = useCallback((product) => {
    setIsToastHidden(false);
    setSubscribingProducts(prev => {
      const exists = prev.some(p => p.sku === product.sku);
      if (exists) return prev;
      return [...prev, { 
        sku: product.sku,
        status: 'subscribing' // subscribing, success, error
      }];
    });
  }, []);

  // Update product subscription status
  const updateProductStatus = useCallback((sku, status, message = '') => {
    setSubscribingProducts(prev =>
      prev.map(p =>
        p.sku === sku ? { ...p, status, message } : p
      )
    );
  }, []);

  // Remove a product from tracking
  const removeSubscribingProduct = useCallback((sku) => {
    setSubscribingProducts(prev => prev.filter(p => p.sku !== sku));
  }, []);

  // Clear all subscriptions
  const clearSubscriptions = useCallback(() => {
    setSubscribingProducts([]);
    setIsToastHidden(false);
    if (toastId) {
      toast.dismiss(toastId);
      setToastId(null);
    }
  }, [toastId]);

  // Hide only the toast card UI, do not clear in-progress subscriptions.
  const hideSubscriptionCard = useCallback(() => {
    setIsToastHidden(true);
    if (toastId) {
      toast.dismiss(toastId);
      setToastId(null);
    }
  }, [toastId]);

  // Calculate progress stats
  const getProgressStats = useCallback(() => {
    const total = subscribingProducts.length;
    const completed = subscribingProducts.filter(p => p.status === 'success').length;
    const inProgress = subscribingProducts.filter(p => p.status === 'subscribing').length;
    const failed = subscribingProducts.filter(p => p.status === 'error').length;
    const progressPercent = total > 0 ? Math.round((completed / total) * 100) : 0;
    
    return { total, completed, inProgress, failed, progressPercent };
  }, [subscribingProducts]);

  // Show or update the consolidated toast with progress
  const updateToast = useCallback((products) => {
    const stats = getProgressStats();
    const toastIcon = stats.inProgress > 0 ? <span className="subscription-toast-loader" /> : false;
    
    const content = (
      <div className="subscription-compact-container">
        {/* Circular Progress with Count */}
        <div className="progress-circle-wrapper">
          <svg className="progress-circle" viewBox="0 0 100 100">
            <defs>
              <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#667eea" />
                <stop offset="100%" stopColor="#764ba2" />
              </linearGradient>
            </defs>
            <circle
              cx="50"
              cy="50"
              r="45"
              className="progress-circle-bg"
            />
            <circle
              cx="50"
              cy="50"
              r="45"
              className="progress-circle-fill"
              style={{
                strokeDasharray: `${(stats.progressPercent / 100) * 283} 283`,
              }}
            />
          </svg>
          <div className="progress-text">
            <div className="progress-count">{stats.completed}/{stats.total}</div>
            {/* <div className="progress-label">Subscribed</div> */}
          </div>
        </div>

        {/* Status Text */}
        <div className="status-text-compact">
          <div className="status-main">Subscribing Products...</div>
          <div className="status-detail">
            {stats.inProgress > 0 && <span>{stats.inProgress} subscribing</span>}
            {stats.failed > 0 && <span className="error-count">{stats.failed} failed</span>}
          </div>
        </div>

        {/* Linear Progress Bar */}
        <div className="linear-progress-container">
          <div className="progress-bar-wrapper">
            <div className="progress-bar-bg">
              <div 
                className="progress-bar-fill" 
                style={{
                  width: `${stats.progressPercent}%`,
                }}
              />
            </div>
          </div>
          <div className="progress-percentage">{stats.progressPercent}%</div>
        </div>

        {/* Close Button */}
        <button
          className="subscription-close-btn-compact"
          onClick={hideSubscriptionCard}
          title="Close"
        >
          ✕
        </button>
      </div>
    );

    if (toastId) {
      toast.update(toastId, {
        render: content,
        type: 'info',
        isLoading: stats.inProgress > 0,
        autoClose: stats.inProgress > 0 ? false : 3000,
        closeButton: false,
        icon: toastIcon,
        className: 'subscription-toast-shell',
        bodyClassName: 'subscription-toast-body',
      });
    } else if (products.length > 0) {
      const id = toast.info(content, {
        autoClose: false,
        closeButton: false,
        icon: toastIcon,
        className: 'subscription-toast-shell',
        bodyClassName: 'subscription-toast-body',
      });
      setToastId(id);
    }
  }, [toastId, hideSubscriptionCard, getProgressStats]);

  // Trigger toast update when products change
  React.useEffect(() => {
    if (isToastHidden) {
      return;
    }

    if (subscribingProducts.length > 0) {
      updateToast(subscribingProducts);
    } else if (toastId) {
      toast.dismiss(toastId);
      setToastId(null);
    }
  }, [subscribingProducts, updateToast, toastId, isToastHidden]);

  const value = {
    subscribingProducts,
    addSubscribingProduct,
    updateProductStatus,
    removeSubscribingProduct,
    clearSubscriptions,
  };

  return (
    <SubscriptionProgressContext.Provider value={value}>
      {children}
    </SubscriptionProgressContext.Provider>
  );
};

export default SubscriptionProgressProvider;
